/* One church: its record, its people, and its lifecycle.
 *
 * TABS IN THE URL. Same reason as the member app — an admin who reloads while
 * looking at Members should land on Members, and a tab should be linkable to
 * a colleague. useTabParam replaces rather than pushes, so Back leaves the
 * church rather than walking the tabs.
 *
 * EDITING SENDS ONLY WHAT CHANGED. PATCH takes every field as optional, so a
 * form that posts all of them would overwrite a field somebody else edited
 * between load and save. The diff below is what makes that safe.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  Pencil,
  Search,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Avatar } from '@/components/ui/avatar'
import { Field, SelectField, TextArea } from '@/components/ui/field'
import { Confirm } from '@/components/ui/confirm'
import {
  useChurch,
  MEMBERS_PAGE_SIZE,
  useChurchMembers,
  useSetChurchStatus,
  useUpdateChurch,
  type SettableStatus,
} from '@/api/console'
import { useTabParam } from '@/lib/useTabParam'
import type { ChurchDetail, ChurchFeatures } from '@/types/api'

import { FEATURE_LABELS, PLAN_OPTIONS } from './CustomFieldsEditor'
import { AwaitingAdmin } from './AwaitingAdmin'
import { JoinQuestionsManager } from '@/features/church/JoinQuestionsPage'
import { Section } from './Section'
import { StatusPill } from './StatusPill'
import { PhoneField } from '@/components/ui/phone-field'
import { DEFAULT_COUNTRY, type Country } from '@/lib/countries'
import { slugProblem } from '@/lib/slug'
import { ApiError } from '@/api/client'

const TABS = ['details', 'fields', 'features', 'members'] as const
type Tab = (typeof TABS)[number]

const TAB_LABEL: Record<Tab, string> = {
  details: 'Details',
  fields: 'Signup fields',
  features: 'Features',
  members: 'Members',
}

export function ChurchDetailPage() {
  const { churchId = '' } = useParams()
  const [tab, setTab] = useTabParam<Tab>(TABS, 'details')
  const church = useChurch(churchId)

  if (church.isLoading) {
    return (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  }

  if (church.isError || !church.data) {
    return (
      <Card className="px-s4 py-s6 text-center text-base text-ink-2">
        Could not load this church.{' '}
        <Link to="/churches" className="font-semibold text-brand hover:underline">
          Back to churches
        </Link>
      </Card>
    )
  }

  const c = church.data

  return (
    <div className="space-y-s4">
      <Link
        to="/churches"
        className="inline-flex items-center gap-s2 text-base font-semibold text-ink-2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Churches
      </Link>

      {/* No actions in the header. Putting Delete beside the church's name
          means the most destructive control on the page sits where the eye
          lands first and a mis-aimed click reaches — it belongs at the bottom,
          past everything somebody actually came here to do. */}
      <header>
        <div className="flex flex-wrap items-center gap-s3">
          <h1 className="text-2xl font-bold tracking-tight">{c.name}</h1>
          <StatusPill status={c.status} />
        </div>
        <p className="mt-1 text-base text-ink-2">
          {[c.city, c.state].filter(Boolean).join(', ') || 'No location set'}
          {' · '}
          {c.member_count} {c.member_count === 1 ? 'member' : 'members'}
        </p>
      </header>

      <SignupLink church={c} />

      {/* Above the tabs, because it is the one thing standing between this
          church and being usable. */}
      {c.status === 'awaiting_admin' ? (
        <AwaitingAdmin
          churchId={c.id}
          churchName={c.name}
          contact={c.admin_mobile || c.admin_email}
        />
      ) : null}

      <div className="flex flex-wrap gap-s1 border-b border-line-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-current={tab === t ? 'page' : undefined}
            className={
              '-mb-px border-b-2 px-s4 py-s3 text-base font-semibold transition-colors ' +
              (tab === t
                ? 'border-brand text-brand'
                : 'border-transparent text-ink-2 hover:text-ink')
            }
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {tab === 'details' ? <DetailsTab church={c} /> : null}
      {tab === 'fields' ? <FieldsTab churchId={churchId} /> : null}
      {tab === 'features' ? <FeaturesTab church={c} /> : null}
      {tab === 'members' ? <MembersTab churchId={churchId} /> : null}

      {/* Lifecycle last, and only on the tab that describes the church itself.
          Repeating it under Members or Features would offer to delete a church
          from a screen that is about something else. */}
      {tab === 'details' ? <StatusActions church={c} /> : null}
    </div>
  )
}

/** The link a church admin uses to register. Worth its own card: it is the one
 *  thing on this page somebody came here to fetch and send on.
 *
 *  EDITABLE, the client's "Group URL … Editable". Changing it does not break
 *  a link already sent: the server keeps the old one pointing here. */
function SignupLink({ church }: { church: ChurchDetail }) {
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(church.slug)
  const update = useUpdateChurch(church.id)
  const url = church.signup_url ?? `/signup/${church.slug}`
  const problem = slugProblem(draft)

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard is blocked without a secure context or permission. The URL
      // is on screen and selectable, so this is a silent no-op rather than an
      // error about something the person can just do by hand.
    }
  }

  function save() {
    const next = draft.trim().toLowerCase()
    if (!next || problem) return
    if (next === church.slug) {
      setEditing(false)
      return
    }
    update.mutate({ slug: next }, { onSuccess: () => setEditing(false) })
  }

  if (editing) {
    return (
      <Card className="space-y-s3 p-s3">
        <Field
          label="Group URL"
          prefix="/signup/"
          prefixWidth={76}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          error={
            problem ??
            (update.error instanceof ApiError ? update.error.message : null)
          }
          help="Links already sent keep working and open this church."
          autoFocus
        />
        <div className="flex gap-s2">
          <Button
            size="sm"
            variant="primary"
            onClick={save}
            loading={update.isPending}
            disabled={!draft.trim() || Boolean(problem)}
          >
            Save
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setDraft(church.slug)
              update.reset()
              setEditing(false)
            }}
          >
            Cancel
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Card className="flex flex-wrap items-center gap-s3 p-s3">
      <span className="text-sm font-semibold uppercase tracking-wide text-ink-2">
        Signup link
      </span>
      <code className="min-w-0 flex-1 truncate text-base">{url}</code>
      <Button size="sm" onClick={copy}>
        {copied ? (
          <>
            <Check className="h-4 w-4" aria-hidden />
            Copied
          </>
        ) : (
          <>
            <Copy className="h-4 w-4" aria-hidden />
            Copy
          </>
        )}
      </Button>
      <Button
        size="sm"
        onClick={() => {
          setDraft(church.slug)
          setEditing(true)
        }}
      >
        <Pencil className="h-4 w-4" aria-hidden />
        Edit
      </Button>
    </Card>
  )
}

/** Hold, restore, delete.
 *
 *  Only the transitions that make sense from where the church stands: an
 *  active church can be put on hold, one on hold can be restored. Rendering
 *  every button and disabling the impossible ones would be noisier and no
 *  clearer.
 *
 *  `awaiting_admin` has its own panel at the top of the page — see
 *  AwaitingAdmin. Activating it here would skip naming an admin. */
function StatusActions({ church }: { church: ChurchDetail }) {
  const setStatus = useSetChurchStatus(church.id)
  const [pending, setPending] = useState<SettableStatus | null>(null)

  /* What each move actually does, in the words of its consequence. "Are you
     sure?" tells somebody nothing; "members cannot sign in" tells them what
     they are about to cause. */
  const COPY: Record<SettableStatus, { title: string; body: string; label: string }> = {
    active: {
      title: `Restore ${church.name}?`,
      body: 'Members will be able to sign in and use the app again.',
      label: 'Restore church',
    },
    on_hold: {
      title: `Put ${church.name} on hold?`,
      body: 'Members keep their accounts and everything they have posted, but cannot use the app until it is restored.',
      label: 'Put on hold',
    },
    deleted: {
      title: `Delete ${church.name}?`,
      body: 'The church is hidden and its members can no longer sign in. Nothing is erased, and it can be restored later.',
      label: 'Delete church',
    },
  }

  const dialog = pending ? COPY[pending] : null

  const onHoldOrDeleted = church.status === 'deleted' || church.status === 'on_hold'

  return (
    <>
      <Card className="border-danger/20 p-s4">
        <h2 className="text-md font-bold">Lifecycle</h2>
        <p className="mt-1 text-base text-ink-2">
          {onHoldOrDeleted
            ? 'This church cannot be used by its members until it is restored.'
            : 'Members keep their accounts and everything they have posted either way. Nothing here erases data.'}
        </p>

        <div className="mt-s3 flex flex-wrap gap-s2">
          {onHoldOrDeleted ? (
            <Button onClick={() => setPending('active')} loading={setStatus.isPending}>
              Restore
            </Button>
          ) : (
            <Button onClick={() => setPending('on_hold')} loading={setStatus.isPending}>
              Put on hold
            </Button>
          )}

          {church.status === 'deleted' ? null : (
            <Button
              variant="danger"
              onClick={() => setPending('deleted')}
              loading={setStatus.isPending}
            >
              Delete
            </Button>
          )}
        </div>
      </Card>

      {dialog ? (
        <Confirm
          open
          onOpenChange={(open) => {
            if (!open) setPending(null)
          }}
          title={dialog.title}
          body={dialog.body}
          confirmLabel={dialog.label}
          tone={pending === 'deleted' ? 'danger' : 'primary'}
          loading={setStatus.isPending}
          onConfirm={() => {
            if (!pending) return
            setStatus.mutate(pending, { onSettled: () => setPending(null) })
          }}
        />
      ) : null}
    </>
  )
}

function DetailsTab({ church }: { church: ChurchDetail }) {
  const update = useUpdateChurch(church.id)

  /* The form starts as a copy of the record and is re-seeded whenever the
     record changes, so a save elsewhere does not leave stale text on screen. */
  const initial = useMemo(
    () => ({
      name: church.name,
      city: church.city ?? '',
      state: church.state ?? '',
      address: church.address ?? '',
      admin_mobile: church.admin_mobile ?? '',
      admin_email: church.admin_email ?? '',
      plan: church.plan,
      welcome_text: church.welcome_text ?? '',
      signature_text: church.signature_text ?? '',
      members_can_see_others: church.members_can_see_others,
    }),
    [church],
  )
  const [adminCountry, setAdminCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [form, setForm] = useState(initial)
  useEffect(() => setForm(initial), [initial])

  const dirty = useMemo(
    () => (Object.keys(initial) as (keyof typeof initial)[]).some(
      (k) => form[k] !== initial[k],
    ),
    [form, initial],
  )

  function save() {
    /* Only what changed. An empty text field means "clear it", which is null
       to the API rather than "" — the server stores null for absent. */
    const changes: Record<string, unknown> = {}
    for (const key of Object.keys(initial) as (keyof typeof initial)[]) {
      if (form[key] === initial[key]) continue
      const value = form[key]
      changes[key] = typeof value === 'string' && !value.trim() ? null : value
    }
    update.mutate(changes)
  }

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  return (
    /* The same grid and the same section headings as Add church. An admin who
       has just created a church should recognise this screen, and the client
       reads both against one document. */
    <Card className="space-y-s5 p-s4">
      <div className="grid gap-s3 sm:grid-cols-2">
        <Field
          label="Name of church"
          className="sm:col-span-2"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          required
        />
        <Field
          label="City"
          value={form.city}
          onChange={(e) => set('city', e.target.value)}
        />
        <Field
          label="State"
          value={form.state}
          onChange={(e) => set('state', e.target.value)}
        />
        <Field
          label="Address"
          className="sm:col-span-2"
          value={form.address}
          onChange={(e) => set('address', e.target.value)}
        />
        {/* The SAME control the member signup uses — see NewChurchPage for
            why a plain text box here caused a real bug. */}
        <PhoneField
          label="Admin Mobile"
          help="Who receives the signup link"
          country={adminCountry}
          onCountryChange={setAdminCountry}
          value={form.admin_mobile}
          onValueChange={(v) => set('admin_mobile', v)}
        />
        <Field
          label="Email"
          type="email"
          value={form.admin_email}
          onChange={(e) => set('admin_email', e.target.value)}
        />
        <SelectField
          label="Subscription plan"
          className="sm:col-span-2"
          value={form.plan}
          onChange={(e) => set('plan', e.target.value)}
          options={PLAN_OPTIONS}
        />
      </div>

      <Section title="Email Welcome Text">
        <TextArea
          label="Welcome text"
          rows={3}
          value={form.welcome_text}
          onChange={(e) => set('welcome_text', e.target.value)}
          help="Sent to each new member when they join this church"
        />
      </Section>

      <Section title="Privacy settings">
        <p className="mb-s2 text-base">
          Can a member see other members and contact?
        </p>
        <div className="flex gap-s5">
          {[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ].map((o) => (
            <label key={String(o.value)} className="flex items-center gap-s2">
              <input
                type="radio"
                name="privacy-detail"
                checked={form.members_can_see_others === o.value}
                onChange={() => set('members_can_see_others', o.value)}
                className="h-4 w-4 accent-[var(--cn-brand)]"
              />
              <span className="text-base">{o.label}</span>
            </label>
          ))}
        </div>
        <p className="mt-s2 text-sm text-ink-2">
          When No, only administrators can see the member list and contact
          details.
        </p>
      </Section>

      <Section title="Signature text">
        <Field
          label="Signature"
          value={form.signature_text}
          onChange={(e) => set('signature_text', e.target.value)}
          help='Defaults to "Yours, <Church Name>"'
        />
      </Section>

      <div className="flex items-center gap-s3">
        <Button
          variant="primary"
          onClick={save}
          disabled={!dirty || !form.name.trim()}
          loading={update.isPending}
        >
          Save changes
        </Button>
        {update.isError ? (
          <span className="text-base text-danger">
            {(update.error as Error).message}
          </span>
        ) : null}
        {update.isSuccess && !dirty ? (
          <span className="text-base text-success-ink">Saved</span>
        ) : null}
      </div>
    </Card>
  )
}

/** The church's own signup questions — the same manager the church admin
 *  uses, so a question added by mistake can be edited or removed here too.
 *  Removing retires it: no longer asked, answers kept. */
function FieldsTab({ churchId }: { churchId: string }) {
  return <JoinQuestionsManager churchId={churchId} />
}

/** The seven feature switches.
 *
 *  Saved one at a time rather than behind a Save button: a switch that stays
 *  where you flicked it and persists is the expected behaviour of a switch,
 *  and there is no partial state worth batching. */
function FeaturesTab({ church }: { church: ChurchDetail }) {
  const update = useUpdateChurch(church.id)

  function toggle(key: keyof ChurchFeatures, on: boolean) {
    // Only the changed key: the server merges it, so a stale copy of the
    // others cannot overwrite a change made elsewhere.
    update.mutate({ features: { [key]: on } })
  }

  return (
    <Card className="space-y-s3 p-s4">
      <p className="text-base text-ink-2">
        Members of this church can only use what is ticked here.
      </p>

      {FEATURE_LABELS.map(([key, label]) => (
        <label key={key} className="flex items-center gap-s3">
          <input
            type="checkbox"
            checked={church.features[key]}
            onChange={(e) => toggle(key, e.target.checked)}
            disabled={update.isPending}
            className="h-4 w-4 accent-[var(--cn-brand)]"
          />
          <span className="font-semibold">{label}</span>
        </label>
      ))}

      {update.isError ? (
        <p className="text-base text-danger">
          {(update.error as Error).message}
        </p>
      ) : null}
    </Card>
  )
}

function MembersTab({ churchId }: { churchId: string }) {
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  /* Cursors of the pages already passed, so Previous goes back the way the
     API came. The first page has none. */
  const [trail, setTrail] = useState<string[]>([])
  const cursor = trail[trail.length - 1] ?? ''
  const members = useChurchMembers(churchId, q, cursor)

  // Searching waits for a pause in typing, and starts again from page one.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search.trim())
      setTrail([])
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  const page = members.data
  const list = page?.items ?? []
  const total = page?.total ?? null
  const from = trail.length * MEMBERS_PAGE_SIZE + 1
  const to = from + list.length - 1

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center gap-s3 border-b border-line-2 p-s3">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
            aria-hidden
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, mobile or email"
            aria-label="Search members"
            className="w-full rounded-pill border border-line-2 bg-surface-2 py-s2 pl-[38px] pr-s3 text-base outline-none focus:shadow-focus"
          />
        </div>
        {total !== null ? (
          <span className="text-sm text-ink-2">
            {total === 0
              ? 'No members'
              : `${from}–${to} of ${total} ${total === 1 ? 'member' : 'members'}`}
          </span>
        ) : null}
      </div>

      {members.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : members.isError ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          Could not load the member list.
        </p>
      ) : !list.length ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          {q ? `Nobody matches “${q}”.` : 'No members yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line-2 text-left text-sm text-ink-2">
                <th className="px-s4 py-s3 font-semibold">NAME</th>
                <th className="px-s4 py-s3 font-semibold">MOBILE</th>
                <th className="px-s4 py-s3 font-semibold">CHURCH ROLE</th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} className="border-b border-line-2 last:border-0">
                  <td className="px-s4 py-s3">
                    <div className="flex items-center gap-s3">
                      <Avatar name={m.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{m.name}</span>
                        {m.email ? (
                          <span className="block truncate text-sm text-ink-2">
                            {m.email}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </td>
                  <td className="px-s4 py-s3 text-base text-ink-2">
                    {m.mobile ?? '—'}
                  </td>
                  <td className="px-s4 py-s3 text-base text-ink-2">
                    {/* Read-only here. Roles belong to the church's own admin,
                        on the member app's Members page — a platform operator
                        editing them from outside would be reaching past the
                        person who actually runs the church. */}
                    {m.role === 'church_admin' || m.role === 'super_admin'
                      ? 'Church Admin'
                      : (m.role_name ?? 'No church role')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {trail.length || page?.has_more ? (
        <div className="flex items-center justify-end gap-s2 border-t border-line-2 p-s3">
          <Button
            variant="secondary"
            size="sm"
            disabled={!trail.length || members.isFetching}
            onClick={() => setTrail((t) => t.slice(0, -1))}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!page?.has_more || !page?.next_cursor || members.isFetching}
            onClick={() => page?.next_cursor && setTrail((t) => [...t, page.next_cursor!])}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : null}
    </Card>
  )
}
